import { Controller } from "@hotwired/stimulus"

// Directory-upgrade "Account setup": seats are driven by the org's existing
// team rather than a slider. Toggling a member for removal updates the billed
// seat count and total live, and stages the removal server-side via auto-save.
// The removals themselves are applied when the upgrade completes.
export default class extends Controller {
  static targets = [
    "memberRow",
    "toggleButton",
    "billedSeats",
    "removedInputs",
    "continueButton"
  ]
  static values = {
    config: Object,
    autoSaveUrl: String,
    baseUrl: String
  }

  declare memberRowTargets: HTMLElement[]
  declare billedSeatsTarget: HTMLElement
  declare removedInputsTarget: HTMLElement
  declare continueButtonTarget: HTMLAnchorElement

  declare configValue: {
    monthly_price?: number
    monthly_tiers?: Array<any>
    base_seat_count?: number
    member_count?: number
  }
  declare autoSaveUrlValue: string
  declare baseUrlValue: string

  private saveTimeout: number | undefined

  connect() {
    this.render()
    this.updateContinueButton()
  }

  toggleMember(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const row = button.closest<HTMLElement>("[data-user-id]")
    if (!row) return

    const removed = row.dataset.removed !== "true"
    row.dataset.removed = removed.toString()
    button.textContent = removed
      ? button.dataset.undoLabel || "Undo"
      : button.dataset.removeLabel || "Remove"

    this.syncRemovedInputs()
    this.render()
    this.updateContinueButton()
    this.scheduleSave()
  }

  private get baseSeatCount(): number {
    return this.configValue.base_seat_count || 2
  }

  private get removedIds(): string[] {
    return this.memberRowTargets
      .filter((row) => row.dataset.removed === "true")
      .map((row) => row.dataset.userId || "")
      .filter(Boolean)
  }

  private get billedSeats(): number {
    const active = this.memberRowTargets.filter((row) => row.dataset.removed !== "true").length
    return Math.max(active, this.baseSeatCount)
  }

  private render() {
    this.billedSeatsTarget.textContent = this.billedSeats.toString()
    this.dispatch("seats", { detail: { quantity: this.billedSeats } })
  }

  private syncRemovedInputs() {
    // Always include a blank sentinel so an empty set still clears the value
    // server-side (Rails drops a fully empty array param).
    const ids = this.removedIds
    this.removedInputsTarget.innerHTML = ""
    ;["", ...ids].forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "removed_user_ids[]"
      input.value = id
      this.removedInputsTarget.appendChild(input)
    })
  }

  private updateContinueButton() {
    const url = new URL(this.baseUrlValue, window.location.origin)
    url.searchParams.set("user_count", this.billedSeats.toString())
    ;["", ...this.removedIds].forEach(id => url.searchParams.append("removed_user_ids[]", id))
    const selected = this.element.querySelector<HTMLInputElement>('input[name="lookup_key"]:checked')
    url.searchParams.set("lookup_key", selected?.value || "standard_monthly")
    const href = url.pathname + url.search
    this.continueButtonTarget.href = href
    this.continueButtonTarget.setAttribute("href", href)
  }

  private scheduleSave() {
    window.clearTimeout(this.saveTimeout)
    this.saveTimeout = window.setTimeout(() => this.save(), 300)
  }

  private async save() {
    const body = new URLSearchParams()
    body.append("removed_user_ids[]", "")
    this.removedIds.forEach((id) => body.append("removed_user_ids[]", id))

    try {
      await fetch(this.autoSaveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json"
        },
        body: body.toString()
      })
    } catch (error) {
      // Best-effort: the authoritative seat count is recomputed server-side at
      // checkout completion, so a dropped auto-save isn't fatal.
      console.warn("Failed to stage team removals", error)
    }
  }

  private get csrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
  }
}
